// A boot PC's hardware in words: what the Boot client reported, as a café owner would say it — "NVIDIA · driver 565",
// "AMD Ryzen 5 7600", "Realtek". Used by the Floor's New PCs tray and wherever the console shows a graphics family.

import type { useTranslation } from '@clavian/i18n/react';

type ConsoleT = ReturnType<typeof useTranslation>['t'];

const GPU_VENDORS: Readonly<Record<string, string>> = { nvidia: 'nvidia', amd: 'amd', ati: 'amd', intel: 'intel' };

/**
 * A graphics family code as words: the vendor, then the driver generation. `nvidia-r565` is "NVIDIA · driver 565",
 * `amd-24.9` "AMD · driver 24.9"; a code with no driver part is the vendor alone, and an unknown vendor is its prefix
 * capitalised. `null` or empty: an empty string (callers show their own "not reported").
 */
export function gpuFamilyLabel(code: string | null | undefined, t: ConsoleT): string {
  const trimmed = (code ?? '').trim();
  if (trimmed === '') return '';
  const cut = trimmed.search(/[-_ ]/);
  const prefix = (cut < 0 ? trimmed : trimmed.slice(0, cut)).toLowerCase();
  const rest = cut < 0 ? '' : trimmed.slice(cut + 1).trim();
  const known = GPU_VENDORS[prefix];
  const vendor = known === undefined ? prefix.charAt(0).toUpperCase() + prefix.slice(1) : t(`boot.hardware.gpu.vendor.${known}`);
  // A leading `r` before digits is how NVIDIA writes its branch ("r565"): the driver is the number.
  const driver = rest.replace(/^r(?=\d)/i, '');
  return driver === '' ? vendor : t('boot.hardware.gpu.withDriver', { vendor, driver });
}

/** A processor name without the trademark and clock noise: "AMD Ryzen 5 7600 6-Core Processor" is "AMD Ryzen 5 7600". */
export function cpuLabel(raw: string | null | undefined): string {
  return (raw ?? '')
    .replace(/\((?:R|TM|C)\)/gi, '')
    .replace(/\bCPU\b/gi, '')
    .replace(/@\s*[\d.]+\s*GHz/gi, '')
    .replace(/\b\d+-Core\b/gi, '')
    .replace(/\bProcessor\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const NIC_VENDORS: Readonly<Record<string, string>> = {
  '8086': 'intel',
  '10EC': 'realtek',
  '1969': 'killer',
  '14E4': 'broadcom',
  '1D6A': 'marvell',
  '15B3': 'mellanox',
};

/** The network card, by the vendor of the first PCI id it reported ("10EC:8125" is "Realtek"); "Network card" otherwise. */
export function nicLabel(pciIds: readonly string[], t: ConsoleT): string | null {
  const first = pciIds[0];
  if (first === undefined) return null;
  const vendor = NIC_VENDORS[first.split(':')[0]?.toUpperCase() ?? ''];
  return vendor === undefined ? t('boot.hardware.nic.other') : t(`boot.hardware.nic.${vendor}`);
}

/** The vendors' long SMBIOS names, as people say them. */
const BOARD_VENDORS: readonly (readonly [RegExp, string])[] = [
  [/^asustek/i, 'ASUS'],
  [/^micro-star/i, 'MSI'],
  [/^gigabyte/i, 'Gigabyte'],
  [/^asrock/i, 'ASRock'],
  [/^biostar/i, 'Biostar'],
  [/^dell/i, 'Dell'],
  [/^hewlett-packard|^hp\b/i, 'HP'],
  [/^lenovo/i, 'Lenovo'],
  [/^intel/i, 'Intel'],
];

type Board = { readonly manufacturer: string; readonly product: string };

/**
 * A motherboard in words (ADR-0065 decision 8): its maker as people say it, then the model — "ASUSTeK COMPUTER INC." ·
 * "PRIME B650M-A" is "ASUS PRIME B650M-A". An unknown maker keeps its own name, trimmed of "Co., Ltd." and the like.
 */
export function motherboardLabel(board: Board | null | undefined): string {
  if (board === null || board === undefined) return '';
  const raw = board.manufacturer.trim();
  const known = BOARD_VENDORS.find(([pattern]) => pattern.test(raw))?.[1];
  const maker = known ?? raw.replace(/[,.]?\s*(co\.?,?\s*ltd\.?|inc\.?|corporation|corp\.?|ltd\.?|gmbh)\s*$/i, '').trim();
  return [maker, board.product.trim()].filter((part) => part !== '').join(' ');
}

/** Two boards are the same model: maker and model, ignoring case and spacing (as the cloud compares them). */
export function sameMotherboard(a: Board, b: Board): boolean {
  const norm = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
  return norm(a.manufacturer) === norm(b.manufacturer) && norm(a.product) === norm(b.product);
}

/** The PC's and its image's boards in words when both are known and differ — the notice's two names — else null. */
export function motherboardMismatch(pc: Board | null | undefined, image: Board | null | undefined): { readonly pc: string; readonly image: string } | null {
  if (pc === null || pc === undefined || image === null || image === undefined || sameMotherboard(pc, image)) return null;
  return { pc: motherboardLabel(pc), image: motherboardLabel(image) };
}

/** The hardware on one line: graphics card, processor, network card — what was reported, in that order. */
export function hardwareLine(pc: { readonly gpuFamily: string | null; readonly cpu: string | null; readonly nicPciIds: readonly string[] }, t: ConsoleT): string {
  return [gpuFamilyLabel(pc.gpuFamily, t), cpuLabel(pc.cpu), nicLabel(pc.nicPciIds, t) ?? '']
    .filter((part) => part !== '')
    .join(' · ');
}
